import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  Calendar,
  User,
  AlertTriangle,
  FileText,
  SlidersHorizontal,
  ChevronDown,
  X,
} from 'lucide-react';
import { gmudService } from '../services/api';
import { GMUD } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import GmudIdBadge from '../components/GmudIdBadge';
import JiraChamadoBadge from '../components/JiraChamadoBadge';
import { formatDateBR } from '../utils/dateUtils';

// Visões rápidas, relativas ao usuário logado
type Visao = 'todas' | 'minhas_pendencias' | 'minhas_gmuds' | 'sou_aprovador' | 'pendentes';
const VISOES_VALIDAS: Visao[] = [
  'todas',
  'minhas_pendencias',
  'minhas_gmuds',
  'sou_aprovador',
  'pendentes',
];

const normalizarEmail = (email?: string | null) => (email || '').trim().toLowerCase();

const GMUDList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gmuds, setGmuds] = useState<GMUD[]>([]);
  const [filteredGmuds, setFilteredGmuds] = useState<GMUD[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [impactoFilter, setImpactoFilter] = useState<string>('todos');
  const [tipoFilter, setTipoFilter] = useState<string>('todos');
  const [dataFilter, setDataFilter] = useState<string>('todos');
  const [aprovadorFilter, setAprovadorFilter] = useState<string>('todos');
  const [jiraFilter, setJiraFilter] = useState('');
  const [situacaoAprovadorFilter, setSituacaoAprovadorFilter] = useState<string>('qualquer');
  const [painelFiltrosAberto, setPainelFiltrosAberto] = useState(false);

  const visaoParam = searchParams.get('visao') as Visao | null;
  const [visao, setVisao] = useState<Visao>(
    visaoParam && VISOES_VALIDAS.includes(visaoParam) ? visaoParam : 'todas'
  );
  // A visão padrão só é escolhida uma vez, no primeiro carregamento
  const visaoPadraoAplicada = useRef(Boolean(visaoParam));
  const meuEmail = normalizarEmail(user?.email);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const mudarVisao = (novaVisao: Visao) => {
    setVisao(novaVisao);
    const params = new URLSearchParams(searchParams);
    params.set('visao', novaVisao);
    setSearchParams(params, { replace: true });
  };

  const fetchGMUDs = async () => {
    try {
      const data = await gmudService.list();
      setGmuds(data);

      // Quem tem pendência de aprovação já entra vendo o que precisa decidir
      if (!visaoPadraoAplicada.current) {
        visaoPadraoAplicada.current = true;
        if (data.some(item => item.aguardandoMinhaAprovacao)) {
          mudarVisao('minhas_pendencias');
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar GMUDs');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const ehResponsavel = useCallback(
    (gmud: GMUD) => gmud.isResponsavel === true || normalizarEmail(gmud.responsavel) === meuEmail,
    [meuEmail]
  );

  // Contagem de cada visão (sempre sobre o total, para o usuário saber o que há em cada uma)
  const contagemVisoes = useMemo(
    () => ({
      todas: gmuds.length,
      minhas_pendencias: gmuds.filter(gmud => gmud.aguardandoMinhaAprovacao).length,
      minhas_gmuds: gmuds.filter(gmud => ehResponsavel(gmud)).length,
      sou_aprovador: gmuds.filter(gmud => gmud.souAprovador).length,
      pendentes: gmuds.filter(gmud => gmud.status === 'pendente').length,
    }),
    [gmuds, ehResponsavel]
  );

  // Aprovadores que aparecem nas GMUDs, para o filtro por aprovador
  const aprovadoresOpcoes = useMemo(() => {
    const porEmail = new Map<string, string>();
    gmuds.forEach(gmud =>
      (gmud.aprovadores || []).forEach(item => {
        const email = normalizarEmail(item.aprovador_email);
        if (email && !porEmail.has(email)) {
          porEmail.set(email, item.aprovador_nome || item.aprovador_email || email);
        }
      })
    );
    return Array.from(porEmail.entries())
      .map(([email, nome]) => ({ email, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [gmuds]);

  const filtrosAtivos =
    visao !== 'todas' ||
    searchTerm !== '' ||
    statusFilter !== 'todos' ||
    impactoFilter !== 'todos' ||
    tipoFilter !== 'todos' ||
    dataFilter !== 'todos' ||
    aprovadorFilter !== 'todos' ||
    jiraFilter.trim() !== '';

  const limparFiltros = () => {
    setSearchTerm('');
    setStatusFilter('todos');
    setImpactoFilter('todos');
    setTipoFilter('todos');
    setDataFilter('todos');
    setAprovadorFilter('todos');
    setJiraFilter('');
    setSituacaoAprovadorFilter('qualquer');
    mudarVisao('todas');
  };

  const filterGMUDs = useCallback(() => {
    let filtered = gmuds;

    // Visão rápida relativa ao usuário logado
    switch (visao) {
      case 'minhas_pendencias':
        filtered = filtered.filter(gmud => gmud.aguardandoMinhaAprovacao);
        break;
      case 'minhas_gmuds':
        filtered = filtered.filter(gmud => ehResponsavel(gmud));
        break;
      case 'sou_aprovador':
        filtered = filtered.filter(gmud => gmud.souAprovador);
        break;
      case 'pendentes':
        filtered = filtered.filter(gmud => gmud.status === 'pendente');
        break;
      default:
        break;
    }

    // Filtro por aprovador (e, opcionalmente, pela situação dele nessa GMUD)
    if (aprovadorFilter !== 'todos') {
      filtered = filtered.filter(gmud =>
        (gmud.aprovadores || []).some(item => {
          if (normalizarEmail(item.aprovador_email) !== aprovadorFilter) return false;
          if (situacaoAprovadorFilter === 'qualquer') return true;
          if (situacaoAprovadorFilter === 'pendente') {
            // só é pendência de fato se a GMUD está aguardando aprovação
            return item.status === 'pendente' && gmud.status === 'pendente';
          }
          return item.status === situacaoAprovadorFilter;
        })
      );
    }

    // Filtro por chamado do Jira (parcial: "SUS", "SUS-12" ou "1234")
    if (jiraFilter.trim()) {
      const chamado = jiraFilter.trim().toLowerCase();
      filtered = filtered.filter(gmud => (gmud.jira_chamado || '').toLowerCase().includes(chamado));
    }

    // Filtro por busca
    if (searchTerm.trim()) {
      const termo = searchTerm.trim().toLowerCase();
      // "#403" (ou "gmud 403") busca somente pelo ID; só números busca pelo ID e também pelo texto
      const porId = termo.match(/^(?:#|gmud[\s-]*#?)\s*(\d+)$/);
      const soNumeros = /^\d+$/.test(termo);

      if (porId) {
        filtered = filtered.filter(gmud => String(gmud.id).startsWith(porId[1]));
      } else {
        filtered = filtered.filter(
          gmud =>
            (soNumeros && String(gmud.id).startsWith(termo)) ||
            gmud.titulo.toLowerCase().includes(termo) ||
            (gmud.jira_chamado || '').toLowerCase().includes(termo) ||
            gmud.responsavel.toLowerCase().includes(termo) ||
            gmud.descricao?.toLowerCase().includes(termo)
        );
      }
    }

    // Filtro por status
    if (statusFilter !== 'todos') {
      filtered = filtered.filter(gmud => gmud.status === statusFilter);
    }

    // Filtro por impacto/risco
    if (impactoFilter !== 'todos') {
      filtered = filtered.filter(gmud => gmud.impacto_risco === impactoFilter);
    }

    // Filtro por tipo de GMUD
    if (tipoFilter !== 'todos') {
      filtered = filtered.filter(gmud => gmud.tipo_gmud === tipoFilter);
    }

    // Filtro por data
    if (dataFilter !== 'todos') {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      filtered = filtered.filter(gmud => {
        const gmudDate = new Date(gmud.data_prevista).toISOString().split('T')[0];

        switch (dataFilter) {
          case 'hoje':
            return gmudDate === todayStr;
          case 'esta_semana':
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return (
              gmudDate >= weekStart.toISOString().split('T')[0] &&
              gmudDate <= weekEnd.toISOString().split('T')[0]
            );
          case 'proxima_semana':
            const nextWeekStart = new Date(today);
            nextWeekStart.setDate(today.getDate() + (7 - today.getDay()));
            const nextWeekEnd = new Date(nextWeekStart);
            nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
            return (
              gmudDate >= nextWeekStart.toISOString().split('T')[0] &&
              gmudDate <= nextWeekEnd.toISOString().split('T')[0]
            );
          case 'atrasadas':
            return gmudDate < todayStr && gmud.status !== 'concluido';
          default:
            return true;
        }
      });
    }

    // Nas pendências do usuário, o que é mais urgente vem primeiro
    if (visao === 'minhas_pendencias') {
      filtered = [...filtered].sort((a, b) => {
        if (a.tipo_gmud !== b.tipo_gmud) return a.tipo_gmud === 'emergencial' ? -1 : 1;
        return String(a.data_prevista).localeCompare(String(b.data_prevista));
      });
    }

    setFilteredGmuds(filtered);
    setCurrentPage(1); // Reset para primeira página quando filtrar
  }, [
    gmuds,
    searchTerm,
    statusFilter,
    impactoFilter,
    tipoFilter,
    dataFilter,
    visao,
    aprovadorFilter,
    jiraFilter,
    situacaoAprovadorFilter,
    ehResponsavel,
  ]);

  // Resumo de aprovações de uma GMUD (para mostrar progresso e quem falta)
  const resumoAprovacao = (gmud: GMUD) => {
    const lista = gmud.aprovadores || [];
    const aprovados = lista.filter(item => item.status === 'aprovado').length;
    const faltam = lista
      .filter(item => item.status === 'pendente')
      .map(item => item.aprovador_nome || item.aprovador_email || '');
    return { total: lista.length, aprovados, faltam };
  };

  // Função para lidar com clique na linha
  const handleRowClick = (gmudId: number, event: React.MouseEvent) => {
    // Evitar navegação se clicar nos botões de ação
    const target = event.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      return;
    }
    navigate(`/gmuds/${gmudId}`);
  };

  // Cálculos de paginação
  const totalPages = Math.ceil(filteredGmuds.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGmuds = filteredGmuds.slice(startIndex, endIndex);

  // Função para mudar página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Função para mudar itens por página
  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchGMUDs();
  }, []);

  useEffect(() => {
    filterGMUDs();
  }, [filterGMUDs]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este GMUD?')) {
      try {
        await gmudService.delete(id);
        toast.success('GMUD excluído com sucesso');
        fetchGMUDs();
      } catch (error) {
        toast.error('Erro ao excluir GMUD');
        console.error('Erro:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concluido':
        return 'badge-success';
      case 'em_execucao':
        return 'badge-warning';
      case 'correcao_solicitada':
        return 'bg-orange-100 text-orange-800';
      case 'aprovado':
        return 'badge-info';
      case 'pendente':
        return 'badge-warning';
      case 'cancelado':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  const getImpactoColor = (impacto: string) => {
    switch (impacto) {
      case 'critico':
        return 'badge-danger';
      case 'alto':
        return 'badge-warning';
      case 'medio':
        return 'badge-info';
      case 'baixo':
        return 'badge-success';
      default:
        return 'badge-info';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'rascunho':
        return 'Rascunho';
      case 'pendente':
        return 'Pendente';
      case 'aprovado':
        return 'Aprovado';
      case 'em_execucao':
        return 'Em Execução';
      case 'correcao_solicitada':
        return 'Correção solicitada';
      case 'concluido':
        return 'Concluído';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getImpactoLabel = (impacto: string) => {
    switch (impacto) {
      case 'critico':
        return 'Crítico';
      case 'alto':
        return 'Alto';
      case 'medio':
        return 'Médio';
      case 'baixo':
        return 'Baixo';
      default:
        return impacto;
    }
  };

  // Filtros em uso (exceto a busca, que já aparece no campo), cada um removível individualmente
  const rotuloVisao: Record<Visao, string> = {
    todas: 'Todas',
    minhas_pendencias: 'Aguardando minha aprovação',
    minhas_gmuds: 'Sou o responsável',
    sou_aprovador: 'Sou aprovador',
    pendentes: 'Pendentes de aprovação (todas)',
  };
  const rotuloData: Record<string, string> = {
    hoje: 'Hoje',
    esta_semana: 'Esta semana',
    proxima_semana: 'Próxima semana',
    atrasadas: 'Atrasadas',
  };
  const rotuloSituacaoAprovador: Record<string, string> = {
    pendente: 'aguardando aprovação',
    aprovado: 'já aprovou',
    rejeitado: 'rejeitou',
  };
  const filtrosAplicados: Array<{ chave: string; rotulo: string; remover: () => void }> = [];
  if (visao !== 'todas') {
    filtrosAplicados.push({
      chave: 'visao',
      rotulo: rotuloVisao[visao],
      remover: () => mudarVisao('todas'),
    });
  }
  if (aprovadorFilter !== 'todos') {
    const nomeAprovador =
      aprovadoresOpcoes.find(item => item.email === aprovadorFilter)?.nome || aprovadorFilter;
    filtrosAplicados.push({
      chave: 'aprovador',
      rotulo: `Aprovador: ${nomeAprovador}${
        situacaoAprovadorFilter !== 'qualquer'
          ? ` (${rotuloSituacaoAprovador[situacaoAprovadorFilter]})`
          : ''
      }`,
      remover: () => {
        setAprovadorFilter('todos');
        setSituacaoAprovadorFilter('qualquer');
      },
    });
  }
  if (jiraFilter.trim()) {
    filtrosAplicados.push({
      chave: 'jira',
      rotulo: `Jira: ${jiraFilter.trim().toUpperCase()}`,
      remover: () => setJiraFilter(''),
    });
  }
  if (statusFilter !== 'todos') {
    filtrosAplicados.push({
      chave: 'status',
      rotulo: `Status: ${getStatusLabel(statusFilter)}`,
      remover: () => setStatusFilter('todos'),
    });
  }
  if (impactoFilter !== 'todos') {
    filtrosAplicados.push({
      chave: 'impacto',
      rotulo: `Impacto: ${getImpactoLabel(impactoFilter)}`,
      remover: () => setImpactoFilter('todos'),
    });
  }
  if (tipoFilter !== 'todos') {
    filtrosAplicados.push({
      chave: 'tipo',
      rotulo: `Tipo: ${tipoFilter === 'emergencial' ? 'Emergencial' : 'Normal'}`,
      remover: () => setTipoFilter('todos'),
    });
  }
  if (dataFilter !== 'todos') {
    filtrosAplicados.push({
      chave: 'data',
      rotulo: `Data: ${rotuloData[dataFilter] || dataFilter}`,
      remover: () => setDataFilter('todos'),
    });
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>GMUDs</h1>
          <p className='mt-2 text-gray-600'>Gerencie todas as mudanças do sistema</p>
        </div>
        <div className='mt-4 sm:mt-0'>
          <Link to='/gmuds/novo' className='btn-primary inline-flex items-center gap-2'>
            <Plus className='h-5 w-5' />
            Novo GMUD
          </Link>
        </div>
      </div>

      {/* Filtros: uma única barra; os combos ficam recolhidos no painel */}
      <div className='card'>
        <div className='flex flex-col sm:flex-row gap-3'>
          {/* Busca */}
          <div className='flex-1'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
              <input
                type='text'
                placeholder='Buscar por #ID, chamado Jira, título, responsável ou descrição...'
                className='input pl-10'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <button
            type='button'
            onClick={() => setPainelFiltrosAberto(aberto => !aberto)}
            aria-expanded={painelFiltrosAberto}
            aria-controls='painel-filtros'
            className={`relative inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              painelFiltrosAberto || filtrosAplicados.length > 0
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal className='h-4 w-4' />
            Filtros
            {filtrosAplicados.length > 0 && (
              <span className='min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-primary-600 text-white text-xs text-center'>
                {filtrosAplicados.length}
              </span>
            )}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${painelFiltrosAberto ? 'rotate-180' : ''}`}
            />
            {contagemVisoes.minhas_pendencias > 0 && visao !== 'minhas_pendencias' && (
              <span
                className='absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-500 border-2 border-white'
                title={`${contagemVisoes.minhas_pendencias} GMUD(s) aguardando a sua aprovação`}
              />
            )}
          </button>
        </div>

        {/* Painel com todos os combos */}
        {painelFiltrosAberto && (
          <div
            id='painel-filtros'
            className='mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'
          >
            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Minha situação</span>
              <select
                className='input'
                value={visao}
                onChange={e => mudarVisao(e.target.value as Visao)}
              >
                <option value='todas'>Todas</option>
                <option value='minhas_pendencias'>
                  Aguardando minha aprovação ({contagemVisoes.minhas_pendencias})
                </option>
                <option value='sou_aprovador'>
                  Sou aprovador ({contagemVisoes.sou_aprovador})
                </option>
                <option value='minhas_gmuds'>
                  Sou o responsável ({contagemVisoes.minhas_gmuds})
                </option>
                <option value='pendentes'>
                  Pendentes de aprovação - todas ({contagemVisoes.pendentes})
                </option>
              </select>
            </label>

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Aprovador</span>
              <select
                className='input'
                value={aprovadorFilter}
                onChange={e => {
                  setAprovadorFilter(e.target.value);
                  if (e.target.value === 'todos') setSituacaoAprovadorFilter('qualquer');
                }}
              >
                <option value='todos'>Todos</option>
                {aprovadoresOpcoes.map(item => (
                  <option key={item.email} value={item.email}>
                    {item.nome}
                    {item.email === meuEmail ? ' (eu)' : ''}
                  </option>
                ))}
              </select>
            </label>

            {aprovadorFilter !== 'todos' && (
              <label className='block'>
                <span className='block text-xs font-medium text-gray-500 mb-1'>
                  Situação do aprovador
                </span>
                <select
                  className='input'
                  value={situacaoAprovadorFilter}
                  onChange={e => setSituacaoAprovadorFilter(e.target.value)}
                >
                  <option value='qualquer'>Qualquer</option>
                  <option value='pendente'>Aguardando a aprovação dele</option>
                  <option value='aprovado'>Já aprovou</option>
                  <option value='rejeitado'>Rejeitou</option>
                </select>
              </label>
            )}

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Chamado Jira</span>
              <input
                type='text'
                className='input uppercase'
                placeholder='Ex.: SUS-1234'
                value={jiraFilter}
                onChange={e => setJiraFilter(e.target.value)}
              />
            </label>

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Status</span>
              <select
                className='input'
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value='todos'>Todos</option>
                <option value='rascunho'>Rascunho</option>
                <option value='pendente'>Pendente</option>
                <option value='aprovado'>Aprovado</option>
                <option value='em_execucao'>Em Execução</option>
                <option value='correcao_solicitada'>Correção solicitada</option>
                <option value='concluido'>Concluído</option>
                <option value='cancelado'>Cancelado</option>
              </select>
            </label>

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Impacto/Risco</span>
              <select
                className='input'
                value={impactoFilter}
                onChange={e => setImpactoFilter(e.target.value)}
              >
                <option value='todos'>Todos</option>
                <option value='baixo'>Baixo</option>
                <option value='medio'>Médio</option>
                <option value='alto'>Alto</option>
                <option value='critico'>Crítico</option>
              </select>
            </label>

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Tipo</span>
              <select
                className='input'
                value={tipoFilter}
                onChange={e => setTipoFilter(e.target.value)}
              >
                <option value='todos'>Todos</option>
                <option value='normal'>Normal</option>
                <option value='emergencial'>Emergencial</option>
              </select>
            </label>

            <label className='block'>
              <span className='block text-xs font-medium text-gray-500 mb-1'>Data prevista</span>
              <select
                className='input'
                value={dataFilter}
                onChange={e => setDataFilter(e.target.value)}
              >
                <option value='todos'>Todas</option>
                <option value='hoje'>Hoje</option>
                <option value='esta_semana'>Esta Semana</option>
                <option value='proxima_semana'>Próxima Semana</option>
                <option value='atrasadas'>Atrasadas</option>
              </select>
            </label>
          </div>
        )}

        {/* Filtros aplicados: cada um se remove com o X */}
        {filtrosAplicados.length > 0 && (
          <div className='mt-4 flex flex-wrap items-center gap-2'>
            {filtrosAplicados.map(filtro => (
              <span
                key={filtro.chave}
                className={`inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium ${
                  filtro.chave === 'visao' && visao === 'minhas_pendencias'
                    ? 'bg-amber-100 text-amber-900'
                    : 'bg-primary-50 text-primary-700'
                }`}
              >
                {filtro.rotulo}
                <button
                  type='button'
                  onClick={filtro.remover}
                  aria-label={`Remover filtro: ${filtro.rotulo}`}
                  className='p-0.5 rounded-full hover:bg-black hover:bg-opacity-10'
                >
                  <X className='h-3 w-3' />
                </button>
              </span>
            ))}
            <button
              type='button'
              onClick={limparFiltros}
              className='text-xs text-gray-600 hover:text-gray-900 underline'
            >
              Limpar tudo
            </button>
          </div>
        )}
      </div>

      {/* Lista de GMUDs */}
      <div className='card'>
        {filteredGmuds.length === 0 ? (
          <div className='text-center py-12'>
            <AlertTriangle className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900'>
              {gmuds.length === 0
                ? 'Nenhum GMUD encontrado'
                : visao === 'minhas_pendencias'
                ? 'Nenhuma GMUD aguardando a sua aprovação'
                : 'Nenhum GMUD corresponde aos filtros'}
            </h3>
            <p className='mt-1 text-sm text-gray-500'>
              {gmuds.length === 0
                ? 'Comece criando seu primeiro GMUD.'
                : visao === 'minhas_pendencias'
                ? 'Você está em dia. Use "Todas pendentes de aprovação" para ver o que falta de outros aprovadores.'
                : 'Tente ajustar os filtros de busca.'}
            </p>
            {gmuds.length > 0 && filtrosAtivos && (
              <button
                type='button'
                onClick={limparFiltros}
                className='mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800'
              >
                <X className='h-4 w-4' />
                Limpar filtros
              </button>
            )}
            {gmuds.length === 0 && (
              <div className='mt-6'>
                <Link to='/gmuds/novo' className='btn-primary inline-flex items-center gap-2'>
                  <Plus className='h-5 w-5' />
                  Criar GMUD
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Versão Desktop */}
            <div className='hidden lg:block overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                      ID
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3'>
                      Título
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      Status
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      Impacto
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                      Tipo
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24'>
                      Data
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      Responsável
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {currentGmuds.map(gmud => (
                    <tr
                      key={gmud.id}
                      className={`cursor-pointer transition-colors ${
                        gmud.aguardandoMinhaAprovacao
                          ? 'bg-amber-50 hover:bg-amber-100'
                          : gmud.aguardandoMinhaCorrecao
                          ? 'bg-orange-50 hover:bg-orange-100'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={e => handleRowClick(gmud.id, e)}
                    >
                      {/* ID */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <div className='flex flex-col items-start gap-1'>
                          <GmudIdBadge id={gmud.id} />
                          <JiraChamadoBadge chamado={gmud.jira_chamado} />
                        </div>
                      </td>

                      {/* Título - Coluna principal */}
                      <td className='px-4 py-3'>
                        <div className='flex items-center'>
                          <div className='flex-shrink-0 h-8 w-8'>
                            <div className='h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center'>
                              <FileText className='h-4 w-4 text-primary-600' />
                            </div>
                          </div>
                          <div className='ml-3 min-w-0 flex-1'>
                            <div
                              className='text-sm font-medium text-gray-900 truncate'
                              title={gmud.titulo}
                            >
                              {gmud.aguardandoMinhaAprovacao && (
                                <span className='mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200 text-amber-900'>
                                  Aguardando você
                                </span>
                              )}
                              {gmud.aguardandoMinhaCorrecao && (
                                <span className='mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-900'>
                                  Corrigir e reenviar
                                </span>
                              )}
                              {gmud.titulo}
                            </div>
                            {gmud.descricao && (
                              <div
                                className='text-xs text-gray-500 truncate'
                                title={gmud.descricao}
                              >
                                {gmud.descricao}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <span className={`badge text-xs ${getStatusColor(gmud.status)}`}>
                          {getStatusLabel(gmud.status)}
                        </span>
                        {gmud.status === 'pendente' && resumoAprovacao(gmud).total > 0 && (
                          <div
                            className='mt-1 text-xs text-gray-500'
                            title={`Aguardando: ${resumoAprovacao(gmud).faltam.join(', ') || '-'}`}
                          >
                            {resumoAprovacao(gmud).aprovados}/{resumoAprovacao(gmud).total} aprovaram
                          </div>
                        )}
                      </td>

                      {/* Impacto/Risco */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <span className={`badge text-xs ${getImpactoColor(gmud.impacto_risco)}`}>
                          {getImpactoLabel(gmud.impacto_risco)}
                        </span>
                      </td>

                      {/* Tipo */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <span
                          className={`badge text-xs ${
                            gmud.tipo_gmud === 'emergencial'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {gmud.tipo_gmud === 'emergencial' ? 'Emerg.' : 'Normal'}
                        </span>
                      </td>

                      {/* Data */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <div className='text-xs text-gray-900'>
                          {formatDateBR(gmud.data_prevista)}
                        </div>
                      </td>

                      {/* Responsável */}
                      <td className='px-3 py-3 whitespace-nowrap'>
                        <div
                          className='text-xs text-gray-900 truncate max-w-20'
                          title={gmud.responsavel}
                        >
                          {gmud.responsavel}
                        </div>
                      </td>

                      {/* Ações */}
                      <td className='px-3 py-3 whitespace-nowrap text-sm font-medium'>
                        <div className='flex items-center space-x-1'>
                          <Link
                            to={`/gmuds/${gmud.id}`}
                            className='text-primary-600 hover:text-primary-900 p-1'
                            title='Visualizar'
                          >
                            <Eye className='h-3 w-3' />
                          </Link>
                          {gmud.status !== 'concluido' && gmud.status !== 'cancelado' && (
                            <Link
                              to={`/gmuds/${gmud.id}/editar`}
                              className='text-warning-600 hover:text-warning-900 p-1'
                              title='Editar'
                            >
                              <Edit className='h-3 w-3' />
                            </Link>
                          )}
                          {gmud.canDelete && (
                            <button
                              onClick={() => handleDelete(gmud.id)}
                              className='text-danger-600 hover:text-danger-900 p-1'
                              title='Excluir'
                            >
                              <Trash2 className='h-3 w-3' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Versão Mobile */}
            <div className='lg:hidden space-y-4'>
              {currentGmuds.map(gmud => (
                <div
                  key={gmud.id}
                  className={`rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer ${
                    gmud.aguardandoMinhaAprovacao
                      ? 'bg-amber-50 border-amber-300'
                      : gmud.aguardandoMinhaCorrecao
                      ? 'bg-orange-50 border-orange-300'
                      : 'bg-white border-gray-200'
                  }`}
                  onClick={e => handleRowClick(gmud.id, e)}
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center mb-2'>
                        <div className='h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center mr-2'>
                          <FileText className='h-3 w-3 text-primary-600' />
                        </div>
                        <GmudIdBadge id={gmud.id} className='mr-2 flex-shrink-0' />
                        <JiraChamadoBadge chamado={gmud.jira_chamado} className='mr-2 flex-shrink-0' />
                        <h3
                          className='text-sm font-medium text-gray-900 truncate'
                          title={gmud.titulo}
                        >
                          {gmud.titulo}
                        </h3>
                      </div>

                      {gmud.descricao && (
                        <p
                          className='text-xs text-gray-500 mb-3 line-clamp-2'
                          title={gmud.descricao}
                        >
                          {gmud.descricao}
                        </p>
                      )}

                      <div className='flex flex-wrap gap-2 mb-3'>
                        {gmud.aguardandoMinhaAprovacao && (
                          <span className='badge text-xs bg-amber-200 text-amber-900'>
                            Aguardando você
                          </span>
                        )}
                        {gmud.aguardandoMinhaCorrecao && (
                          <span className='badge text-xs bg-orange-200 text-orange-900'>
                            Corrigir e reenviar
                          </span>
                        )}
                        <span className={`badge text-xs ${getStatusColor(gmud.status)}`}>
                          {getStatusLabel(gmud.status)}
                        </span>
                        {gmud.status === 'pendente' && resumoAprovacao(gmud).total > 0 && (
                          <span
                            className='badge text-xs bg-gray-100 text-gray-700'
                            title={`Aguardando: ${resumoAprovacao(gmud).faltam.join(', ') || '-'}`}
                          >
                            {resumoAprovacao(gmud).aprovados}/{resumoAprovacao(gmud).total} aprovaram
                          </span>
                        )}
                        <span className={`badge text-xs ${getImpactoColor(gmud.impacto_risco)}`}>
                          {getImpactoLabel(gmud.impacto_risco)}
                        </span>
                        <span
                          className={`badge text-xs ${
                            gmud.tipo_gmud === 'emergencial'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {gmud.tipo_gmud === 'emergencial' ? 'Emergencial' : 'Normal'}
                        </span>
                      </div>

                      <div className='flex items-center justify-between text-xs text-gray-500'>
                        <div className='flex items-center'>
                          <Calendar className='h-3 w-3 mr-1' />
                          {formatDateBR(gmud.data_prevista)}
                        </div>
                        <div className='flex items-center'>
                          <User className='h-3 w-3 mr-1' />
                          <span className='truncate max-w-24' title={gmud.responsavel}>
                            {gmud.responsavel}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center space-x-1 ml-2'>
                      <Link
                        to={`/gmuds/${gmud.id}`}
                        className='text-primary-600 hover:text-primary-900 p-1'
                        title='Visualizar'
                        onClick={e => e.stopPropagation()}
                      >
                        <Eye className='h-4 w-4' />
                      </Link>
                      {gmud.status !== 'concluido' && gmud.status !== 'cancelado' && (
                        <Link
                          to={`/gmuds/${gmud.id}/editar`}
                          className='text-warning-600 hover:text-warning-900 p-1'
                          title='Editar'
                          onClick={e => e.stopPropagation()}
                        >
                          <Edit className='h-4 w-4' />
                        </Link>
                      )}
                      {gmud.canDelete && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(gmud.id);
                          }}
                          className='text-danger-600 hover:text-danger-900 p-1'
                          title='Excluir'
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Controles de paginação */}
      {filteredGmuds.length > 0 && (
        <div className='bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6'>
          <div className='flex-1 flex justify-between sm:hidden'>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Anterior
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Próximo
            </button>
          </div>
          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div className='flex items-center space-x-2'>
              <span className='text-sm text-gray-700'>
                Mostrando <span className='font-medium'>{startIndex + 1}</span> até{' '}
                <span className='font-medium'>{Math.min(endIndex, filteredGmuds.length)}</span> de{' '}
                <span className='font-medium'>{filteredGmuds.length}</span> resultados
              </span>
              <select
                value={itemsPerPage}
                onChange={e => handleItemsPerPageChange(Number(e.target.value))}
                className='ml-2 border border-gray-300 rounded-md px-2 py-1 text-sm'
              >
                <option value={5}>5 por página</option>
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
              </select>
            </div>
            <div>
              <nav
                className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                aria-label='Pagination'
              >
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <span className='sr-only'>Anterior</span>←
                </button>

                {/* Páginas */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <span className='sr-only'>Próximo</span>→
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GMUDList;
